import apiClient from './api';
import { Upload, UploadIssue, UploadStartRequest, WorkspaceRights } from '@/types/upload';
import { UPLOAD_TIMEOUT_MS } from '@/config/upload';

/** A workspace's uploads (geonexora-server /api/v2/workspaces/{workspace}/uploads). */
const base = (workspace: string) => `/v2/workspaces/${workspace}/uploads`;

class UploadService {
  /** What the caller may do in the workspace, so the screen hides what would be refused. */
  async rights(workspace: string): Promise<WorkspaceRights> {
    return (await apiClient.get<WorkspaceRights>(`/v2/workspaces/${workspace}/rights`)).data;
  }

  /** Sends a file; the server reads it for the preview and answers with the upload, WAITING. */
  async upload(workspace: string, file: File): Promise<Upload> {
    const form = new FormData();
    form.append('file', file);
    // postForm sends multipart/form-data; a plain post would turn the FormData into JSON
    const response = await apiClient.postForm<Upload>(base(workspace), form, { timeout: UPLOAD_TIMEOUT_MS });
    return response.data;
  }

  /** The workspace's uploads, newest first. */
  async list(workspace: string): Promise<Upload[]> {
    return (await apiClient.get<Upload[]>(base(workspace))).data;
  }

  async get(workspace: string, id: string): Promise<Upload> {
    return (await apiClient.get<Upload>(`${base(workspace)}/${id}`)).data;
  }

  /** Starts the import with the column check's choices; the server answers at once, QUEUED. */
  async start(workspace: string, id: string, request: UploadStartRequest): Promise<Upload> {
    return (await apiClient.post<Upload>(`${base(workspace)}/${id}/start`, request)).data;
  }

  async issues(workspace: string, id: string, limit = 200): Promise<UploadIssue[]> {
    return (await apiClient.get<UploadIssue[]>(`${base(workspace)}/${id}/issues`, { params: { limit } })).data;
  }

  /** A waiting upload withdrawn, or a finished one taken off the list (the layer it made stays). */
  async remove(workspace: string, id: string): Promise<void> {
    await apiClient.delete(`${base(workspace)}/${id}`);
  }
}

export const uploadService = new UploadService();
export default uploadService;
